import { IHome } from '@/interfaces/home';
import { IFinalProductProps } from '@/interfaces/products';
import { compose, flatten, isBoolean, isNil, join, reject } from 'lodash/fp';
import { has } from 'lodash';
import GoogleIcon from '@/static/images/social-icons/google.svg';
import FacebookIcon from '@/static/images/social-icons/facebook.svg';
import InstagramIcon from '@/static/images/social-icons/instagram.svg';
import TripadvisorIcon from '@/static/images/social-icons/tripadvisor.svg';
import ExternalLinkIcon from '@/static/images/social-icons/external-link.svg';

export const changeLocation = (link: string) => {
  if (!link.trim()) return '/';

  if (link.startsWith('http') || link.startsWith('www.')) {
    window.location.href = link;
    return;
  }

  window.location.pathname = mountUrl(link);
};

export const mountUrl = (link: string) => {
  if (!link.trim()) return '/';

  return link.startsWith('/') ? `${link}/` : `/${link}/`;
};

export const classNames = (...args: unknown[]) =>
  compose(join(' '), reject(isBoolean), reject(isNil), flatten)(args);

export const getContrastedColor = (shouldApplyContrast: boolean) => {
  return {
    navBarItemTextColor: {
      color: shouldApplyContrast ? '#000' : '#fff',
    },
    navBarImageColor: {
      filter: shouldApplyContrast ? 'brightness(0%)' : '',
    },
  };
};

export const hasPropertiesWithValue = (obj: unknown | null | undefined) => {
  if (!obj) return false;

  return Object.values(obj).some(
    (value) => value !== null && value !== undefined && value !== ''
  );
};

const getInitialSlideItem = (slides: unknown[], initial?: number) => {
  return slides.length > 2 ? initial ?? 1 : 0;
};

export const getTestimonialsSlidesBreakPoints = (slides: unknown[]) => ({
  2000: {
    slidesPerView: 5,
    spaceBetween: 80,
    initialSlide: getInitialSlideItem(slides, 2),
  },
  1600: {
    slidesPerView: 4,
    spaceBetween: 80,
    initialSlide: getInitialSlideItem(slides, 2),
  },
  1285: {
    slidesPerView: 2,
    spaceBetween: 70,
    initialSlide: 1,
  },
  800: {
    slidesPerView: 2,
    spaceBetween: 60,
    initialSlide: 0,
  },
  500: {
    slidesPerView: 1,
    initialSlide: 0,
  },
});

const filterProductsAndPackages = (
  product: IFinalProductProps,
  limitPackages: boolean,
  limitProducts: boolean,
  homeProductsId: string[],
  homePackagesId: string[]
) => {
  const isPackage = product.packageId !== undefined;

  const isProductLimited = limitProducts || homeProductsId.length === 0;
  const isPackageLimited = limitPackages || homePackagesId.length === 0;

  const isPackageHighlighted =
    product.packageId && homePackagesId.includes(product.packageId);
  const isProductHighlighted =
    product.id && homeProductsId.includes(product.id);

  if (isPackage && isPackageLimited) {
    return product;
  }

  if (!isPackage && isProductLimited) {
    return product;
  }

  return isPackageHighlighted || isProductHighlighted;
};

const sortByIdOrder = (
  referenceIdList: string[],
  extractId: (product: IFinalProductProps) => string
) => {
  return function (a: IFinalProductProps, b: IFinalProductProps) {
    const aId = extractId(a);
    const bId = extractId(b);
    const aIndex = referenceIdList ? referenceIdList.indexOf(`${aId}`) : -1;
    const bIndex = referenceIdList ? referenceIdList.indexOf(`${bId}`) : -1;
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  };
};

export const getHomeProductList = (
  products: IFinalProductProps[],
  home: IHome
) => {
  const {
    productList,
    mainProductId,
    mainProductPackageId,
    notListedPackages,
    notListedProducts,
  } = home;
  const homeProductsId = productList.homeProducts || [];
  const homePackagesId = productList.homePackages || [];

  const limitProducts =
    homeProductsId && homeProductsId.find((id) => id === null) === null;
  const limitPackages =
    homePackagesId && homePackagesId.find((id) => id === null) === null;

  let allowedProducts = products.filter((product) =>
    filterProductsAndPackages(
      product,
      limitPackages,
      limitProducts,
      homeProductsId,
      homePackagesId
    )
  );

  if (!productList.repeatMain) {
    allowedProducts = allowedProducts.filter(
      (product) =>
        product.id !== mainProductId ||
        product.packageId !== mainProductPackageId
    );
  }

  if (notListedProducts) {
    allowedProducts = allowedProducts.filter(
      (product) => !notListedProducts.productsId.includes(product.id!)
    );
  }

  if (notListedPackages) {
    allowedProducts = allowedProducts.filter(
      (product) =>
        !notListedPackages.packagesId.includes(product.packageId ?? '')
    );
  }

  let sortedProducts: IFinalProductProps[] = [];
  let sortedPackages: IFinalProductProps[] = [];

  allowedProducts.forEach((product) => {
    if (product.packageId) {
      sortedPackages.push(product);
    } else {
      sortedProducts.push(product);
    }
  });

  sortedProducts.sort(sortByIdOrder(homeProductsId, (p) => p.id!));

  sortedPackages.sort(sortByIdOrder(homePackagesId, (p) => p.packageId!));

  return [...sortedProducts, ...sortedPackages];
};

export const addOrRemoveClass = (targetClass: string, selector: string) => {
  const el = document.querySelector(selector);

  if (!el) return;

  if (el.classList.contains(targetClass)) {
    el.classList.remove(targetClass);
  } else {
    el.classList.add(targetClass);
  }
};

export const hasProperty = (obj: object, props: string[]) =>
  props.some((prop) => has(obj, prop));

export const getTestimonialIcon = (icon: string) => {
  switch (icon) {
    case 'google':
      return GoogleIcon;
    case 'facebook':
      return FacebookIcon;
    case 'instagram':
      return InstagramIcon;
    case 'tripadvisor':
      return TripadvisorIcon;

    default:
      return ExternalLinkIcon;
  }
};

export const serializeToQueryString = (obj: { [key: string]: string }) => {
  const str: string[] = [];

  Object.keys(obj).forEach((p) => {
    if (p in obj) {
      if (obj[p]) {
        str.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
      }
    }
  });

  return str.join('&');
};
